// frontend/src/components/ScoreHero.js
import ScoreGauge from "./ScoreGauge";
import { SEVERITY, SEVERITY_ORDER } from "../lib/theme";
import { toast } from "../lib/toast";

export default function ScoreHero({
  domain,
  score,
  grade,
  summary,
  counts,
  scannedAt,
  scanId,
  source,
  previous,
  onRescan,
}) {
  const delta = previous ? score - previous.score : 0;

  // Band colours tuned for a dark surface — same meaning, different values
  // from the light-theme chips so they are visible against slate-900.
  function bandLabel(score) {
    if (score >= 80) return { label: "Excellent", chip: "border-emerald-400/40 bg-emerald-500/15 text-emerald-300", ring: "#34d399", track: "rgba(52, 211, 153, 0.15)" };
    if (score >= 60) return { label: "Good",      chip: "border-amber-400/40 bg-amber-500/15 text-amber-300",     ring: "#fbbf24", track: "rgba(251, 191, 36, 0.15)" };
    if (score >= 40) return { label: "Fair",      chip: "border-orange-400/40 bg-orange-500/15 text-orange-300", ring: "#fb923c", track: "rgba(251, 146, 60, 0.15)" };
    return               { label: "At risk",  chip: "border-rose-400/40 bg-rose-500/15 text-rose-300",       ring: "#fb7185", track: "rgba(251, 113, 133, 0.15)" };
  }
  const band = bandLabel(score);

  async function handleShare() {
    const url = new URL(window.location.href);
    url.searchParams.set("domain", domain);
    try {
      await navigator.clipboard.writeText(url.toString());
      toast("Share link copied", { kind: "success" });
    } catch {
      window.prompt("Copy this link:", url.toString());
    }
  }

  return (
    <section className="relative overflow-hidden rounded-3xl border border-slate-800 bg-slate-900 p-6 shadow-xl shadow-slate-900/20 cc-fade-in sm:p-8">
      {/* Soft ambient glows, tinted to the score band */}
      <div
        className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full opacity-25 blur-3xl"
        style={{ background: band.ring }}
        aria-hidden="true"
      />
      <div
        className="pointer-events-none absolute -bottom-32 -left-24 h-72 w-72 rounded-full bg-slate-700 opacity-25 blur-3xl"
        aria-hidden="true"
      />

      <div className="relative flex flex-col items-start gap-6 sm:flex-row sm:items-center sm:gap-8">
        <ScoreGauge
          score={score}
          grade={grade}
          ringColor={band.ring}
          ringTrack={band.track}
          size={170}
        />

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ${band.chip}`}
            >
              {band.label}
            </span>

            {previous && delta !== 0 && (
              <span
                className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-semibold ${
                  delta > 0
                    ? "border-emerald-400/40 bg-emerald-500/15 text-emerald-300"
                    : "border-rose-400/40 bg-rose-500/15 text-rose-300"
                }`}
                title={`Previous score: ${previous.score}`}
              >
                {delta > 0 ? "▲" : "▼"} {Math.abs(delta)} since last scan
              </span>
            )}

            {source === "demo" && (
              <span
                className="rounded-full border border-amber-400/40 bg-amber-500/15 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-amber-300"
                title="Live scanner unavailable — showing sample data."
              >
                Demo data
              </span>
            )}
          </div>

          <h2 className="mt-3 truncate font-mono text-lg font-semibold tracking-tight text-white sm:text-xl">
            {domain}
          </h2>

          <p className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-400">
            <span>Scanned {new Date(scannedAt).toLocaleString()}</span>
            {scanId && (
              <>
                <span aria-hidden="true">·</span>
                <span className="font-mono">ID {scanId}</span>
              </>
            )}
            <span
              className="inline-flex items-center gap-1 rounded-full border border-emerald-400/40 bg-emerald-500/15 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-emerald-300"
              title="Passive checks only. No logins, no exploitation, no port scanning."
            >
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
              Non-invasive
            </span>
          </p>

          <p className="mt-3.5 max-w-xl text-sm leading-relaxed text-slate-200 cc-prose">
            {summary}
          </p>

          <div className="mt-5 flex flex-wrap gap-2">
            {SEVERITY_ORDER.filter((s) => counts[s] > 0).map((s) => {
              const meta = SEVERITY[s];
              // Each severity uses a dark-surface variant so the chips stay
              // readable against slate-900.
              return (
                <span
                  key={s}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${severityChip(s)}`}
                >
                  <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} />
                  <span className="tabular-nums">{counts[s]}</span>
                  <span className="opacity-90">{meta.label}</span>
                </span>
              );
            })}
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={onRescan}
              className="rounded-xl bg-white px-4 py-2 text-xs font-semibold text-slate-900 shadow-sm transition hover:-translate-y-0.5 hover:bg-amber-300 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900"
            >
              Re-scan
            </button>
            <button
              type="button"
              onClick={handleShare}
              className="rounded-xl border border-slate-700 bg-slate-800 px-4 py-2 text-xs font-medium text-slate-100 transition hover:-translate-y-0.5 hover:border-slate-500 hover:bg-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-900"
            >
              Copy share link
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

// Dark-surface chip colours — one per severity.
function severityChip(severity) {
  switch (severity) {
    case "critical": return "border-rose-400/40 bg-rose-500/15 text-rose-300";
    case "high":     return "border-orange-400/40 bg-orange-500/15 text-orange-300";
    case "medium":   return "border-amber-400/40 bg-amber-500/15 text-amber-300";
    case "low":      return "border-yellow-400/40 bg-yellow-500/15 text-yellow-200";
    case "info":     return "border-sky-400/40 bg-sky-500/15 text-sky-300";
    case "pass":     return "border-emerald-400/40 bg-emerald-500/15 text-emerald-300";
    default:         return "border-slate-600/50 bg-slate-700/40 text-slate-300";
  }
}